import {
  useGetDevice,
  useGetDocuments,
  useGetDocumentsTemplates,
  useGetUser,
} from "@hooks";
import { AssistantProcessStatus } from "@models";
import { Skeleton, Typography } from "@mui/material";
import { observer } from "mobx-react-lite";
import type { ReactNode } from "react";
import { useMemo } from "react";
import DiffViewer from "src/components/DocViewer/components/markdownDocViewer/diffViewer/DiffViewer";
import { useGetAssistantProcesses } from "src/hooks/queries/assistant-process";
import {
  getSystemDataKeyValue,
  isOrganizationSystemDataKey,
  isSystemDataKey,
} from "src/utils/system-data-keys";
import { getOrgFromUser } from "src/utils/user";
import { MarkdownText } from "../../../ui/MarkdownText";
import { isHumanInTheLoopRejection } from "../../human-in-the-loop";
import type { ChatToolMessage } from "../../types";
import { useChatDeviceContext } from "../../useChatDeviceContext";
import { keyToLabel } from "../utils/utils";
import { getEditDataKeyCurrentValue } from "./editDataKeyCurrentValue";
import { resolveEditDataKeySource } from "./editDataKeySource";
import { getPartialEditDiffDisplay } from "./editDataKeyValue";
import { EditToolCallFrame } from "./EditToolCallFrame";
import { getToolDeviceId } from "./toolDeviceId";

export interface EditDataKeyToolProps {
  args: Record<string, any>;
  toolResult?: ChatToolMessage;
  isLoading?: boolean;
  decision?: "approve" | "reject" | "edit";
  approvalControls?: (options: { approveDisabled: boolean }) => ReactNode;
}

const EditDataKeyTool = observer(
  ({
    args,
    toolResult,
    isLoading = false,
    decision,
    approvalControls,
  }: EditDataKeyToolProps) => {
    const { orgId } = useChatDeviceContext();
    const deviceId = getToolDeviceId(args);
    const dataKeyId = typeof args.id === "string" ? args.id : "";
    const source = useMemo(() => resolveEditDataKeySource(args), [args]);
    const isSystemKey = isSystemDataKey(dataKeyId);

    const { data: user, isError: userError } = useGetUser();
    const organization = getOrgFromUser(user, orgId);
    const { data: device, isError: deviceError } = useGetDevice({
      orgId,
      deviceId,
    });

    const { data: assistantProcesses, isError: assistantProcessesError } =
      useGetAssistantProcesses({
        orgId,
        deviceId,
        status: AssistantProcessStatus.IN_PROGRESS,
        enabled:
          !isSystemKey &&
          source.kind === "assistant-process" &&
          !!orgId &&
          !!deviceId,
      });

    const { data: templatesConfig, isError: templatesError } =
      useGetDocumentsTemplates({
        orgId,
        deviceId,
        enabled: !isSystemKey && source.kind === "unscoped" && !!deviceId,
      });

    const { data: documents, isError: documentsError } = useGetDocuments(
      orgId,
      deviceId
    );

    const title = dataKeyId ? `Edit ${keyToLabel(dataKeyId)}` : "Edit data key";

    const currentValueResolution = useMemo(() => {
      if (isSystemKey) {
        const contextLoaded = isOrganizationSystemDataKey(dataKeyId)
          ? !!organization
          : !!device;
        return contextLoaded
          ? {
              value: getSystemDataKeyValue({ dataKeyId, organization, device }),
              warning: null,
            }
          : null;
      }

      return getEditDataKeyCurrentValue({
        dataKeyId,
        documents,
        templatesConfig,
        assistantProcesses,
        source,
      });
    }, [
      assistantProcesses,
      dataKeyId,
      documents,
      device,
      organization,
      isSystemKey,
      source,
      templatesConfig,
    ]);
    const currentValue = currentValueResolution?.value;

    const isRejected = isHumanInTheLoopRejection(toolResult);
    const hasError = toolResult?.status === "error" && !isRejected;
    const hasToolResult = !!toolResult?.tool_call_id;
    const hasPartialEdit =
      typeof args.old_string === "string" &&
      typeof args.new_string === "string";
    const nextValue =
      typeof args.value === "string"
        ? args.value
        : hasPartialEdit
          ? args.new_string
          : null;

    const needsOriginalValue = !hasPartialEdit && !hasToolResult;
    const originalValueLoadFailed =
      needsOriginalValue &&
      (isSystemKey
        ? isOrganizationSystemDataKey(dataKeyId)
          ? userError
          : deviceError
        : source.kind === "assistant-process"
          ? assistantProcessesError
          : source.kind === "document"
            ? documentsError
            : source.kind === "unscoped"
              ? documentsError || templatesError
              : false);
    const originalValueUnavailable =
      needsOriginalValue &&
      (originalValueLoadFailed ||
        !currentValueResolution ||
        !!currentValueResolution.warning);
    const fullValueDiffDisplay =
      needsOriginalValue &&
      !originalValueUnavailable &&
      nextValue !== null &&
      currentValue !== undefined &&
      currentValue !== nextValue
        ? { currentMarkdown: nextValue, previousMarkdown: currentValue }
        : null;
    const diffDisplay = getPartialEditDiffDisplay(args) ?? fullValueDiffDisplay;
    const hasNextValue = nextValue !== null;
    const hasDiff = diffDisplay !== null;
    const warning =
      hasPartialEdit || isLoading || hasError
        ? null
        : hasToolResult && hasNextValue
          ? "Original value unavailable. Showing the saved value."
          : currentValueResolution?.warning;

    return (
      <EditToolCallFrame
        toolName="edit_data_key"
        title={title}
        label={title}
        decision={decision}
        isLoading={isLoading && !hasToolResult}
        hasError={hasError}
        isRejected={isRejected}
        hasToolResult={hasToolResult}
        hasContent={hasError || hasDiff || hasNextValue || !!warning}
        controls={
          !hasToolResult &&
          approvalControls?.({
            approveDisabled: !hasNextValue || originalValueUnavailable,
          })
        }
      >
        {isRejected ? null : hasError ? (
          <Typography className="text-red-500 text-sm">
            {typeof toolResult?.content === "string"
              ? toolResult.content
              : "The edit could not be applied."}
          </Typography>
        ) : (
          <div className="space-y-3">
            {originalValueLoadFailed ? (
              <Typography className="text-red-500 text-sm">
                Could not load the original value.
              </Typography>
            ) : needsOriginalValue && !currentValueResolution ? (
              <Skeleton
                variant="rectangular"
                height={64}
                aria-label="Loading original value"
              />
            ) : hasDiff ? (
              <DiffViewer
                currentMarkdown={diffDisplay.currentMarkdown}
                previousMarkdown={diffDisplay.previousMarkdown}
              />
            ) : hasNextValue ? (
              <MarkdownText>{nextValue}</MarkdownText>
            ) : (
              <Typography className="text-gray-500 text-sm">
                Waiting for edit payload...
              </Typography>
            )}
            {warning && (
              <Typography className="text-amber-600 text-sm">
                {warning}
              </Typography>
            )}
          </div>
        )}
      </EditToolCallFrame>
    );
  }
);

export default EditDataKeyTool;

EditDataKeyTool.displayName = "EditDataKeyTool";
